import React, {
  createContext,
  useContext,
  useCallback,
  useState,
  useMemo,
  useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from '@material-ui/core';
import moment from 'moment';
import { useSnackbar } from 'notistack';

import Actions from '../../../store/ducks/appointments';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import ProfessionalModal from './ProfessionalModal';
import RatingModal from './RatingModal';

const AppointmentContext = createContext();

const AppointmentProvider = ({ children }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const {
    infos: infosAppointments,
    history: { list: appointments },
  } = useSelector(state => state.appointments);
  const [cancelModal, setCancelModal] = useState(null);
  const [
    cancelAppointmentRequestedId,
    setCancelAppointmentRequestedId,
  ] = useState(null);
  const [professionalModal, setProfessionalModal] = useState(null);
  const [ratingModal, setRatingModal] = useState(null);

  useEffect(() => {
    if (cancelAppointmentRequestedId) {
      const error = appointments[cancelAppointmentRequestedId]?.error;
      const success = appointments[cancelAppointmentRequestedId]?.success;
      if (error?.action === 'cancelAppointment') {
        enqueueSnackbar(error.msg, { variant: 'error' });
        setCancelAppointmentRequestedId(null);
      }
      if (success?.action === 'cancelAppointment') {
        enqueueSnackbar(success.msg, { variant: 'success' });
        setCancelAppointmentRequestedId(null);
        setCancelModal(null);
      }
    }
  }, [cancelAppointmentRequestedId, appointments, enqueueSnackbar]);

  const requestCancelAppointment = useCallback(
    id => {
      if (!cancelAppointmentRequestedId) {
        dispatch(Actions.requestCancelAppointment(id));
        setCancelAppointmentRequestedId(id);
      }
    },
    [dispatch, cancelAppointmentRequestedId],
  );

  const limitPeriodType = useMemo(() => {
    const period = infosAppointments?.limitConsultant?.periodType;
    switch (period) {
      case 'day':
        return 'diário';
      case 'week':
        return 'semanal';
      case 'month':
        return 'mensal';
      case 'only once':
        return 'único';
      default:
        return '';
    }
  }, [infosAppointments]);

  const handleCancelAppointment = useCallback(
    (id, allowToCancel) => {
      setCancelModal({
        title: allowToCancel
          ? 'Tem certeza que deseja cancelar o agendamento?'
          : 'Tem certeza que deseja informar sua ausência?',
        description: allowToCancel
          ? ''
          : `Mesmo informando sua ausência, este agendamento irá descontar ${
              limitPeriodType !== 'único'
                ? `parte do seu limite ${limitPeriodType} de agendamentos`
                : 'o seu limite único de agendamento'
            }. Para cancelar sem descontar, você deve cancelar com no mínimo 48h de antecedência.`,
        confirmText: allowToCancel
          ? 'Confirmar cancelamento'
          : 'Informar ausência',
        confirmCb: () => requestCancelAppointment(id),
      });
    },
    [requestCancelAppointment, limitPeriodType],
  );

  const handleShowProfessionalModal = useCallback(professionalId => {
    setProfessionalModal({
      professionalId,
      handleExited: () => setProfessionalModal(null),
    });
  }, []);

  const handleShowRatingModal = useCallback(appointmentId => {
    setRatingModal({
      appointmentId,
      handleExited: () => setRatingModal(null),
    });
  }, []);

  const getAppointmentActions = useCallback(
    (id, skypeConsultant, statusType, date, professionalId) => {
      const timeToHappenMore48h = moment(date).diff(moment(), 'hours') >= 48;
      return [
        {
          icon: 'launch',
          title: 'Ir para a sala',
          action: null,
          disabled: statusType !== 'SCHEDULED',
          props: {
            component: Link,
            href: skypeConsultant,
            target: '_blank',
          },
        },
        {
          icon: 'perm_contact_calendar',
          title: 'Sobre o profissional',
          action: () => handleShowProfessionalModal(professionalId),
          disabled: false,
        },
        {
          icon: 'cancel',
          title: timeToHappenMore48h
            ? 'Cancelar agendamento'
            : 'Informar ausência',
          action: () => handleCancelAppointment(id, timeToHappenMore48h),
          disabled: statusType !== 'SCHEDULED',
        },
        {
          icon: 'star_purple',
          title: 'Avaliação do agendamento',
          action: () => handleShowRatingModal(id),
          disabled: statusType !== 'CONCLUDED',
        },
      ];
    },
    [
      handleCancelAppointment,
      handleShowProfessionalModal,
      handleShowRatingModal,
    ],
  );

  return (
    <AppointmentContext.Provider
      value={{
        getAppointmentActions,
        handleCancelAppointment,
        handleShowProfessionalModal,
        handleShowRatingModal,
      }}
    >
      {children}
      <ConfirmationDialog
        type="warning"
        icon="warning"
        cancelText="Fechar"
        cancelCb={() => setCancelModal(null)}
        isOpen={!!cancelModal}
        loading={!!cancelAppointmentRequestedId}
        {...cancelModal}
      />
      {!!professionalModal && <ProfessionalModal {...professionalModal} />}
      {!!ratingModal && <RatingModal {...ratingModal} />}
    </AppointmentContext.Provider>
  );
};

function useAppointment() {
  const context = useContext(AppointmentContext);
  if (!context) {
    throw new Error(
      'useAppointment must be used within an AppointmentProvider',
    );
  }
  return context;
}

export { AppointmentProvider, useAppointment };
